import { Box, useTheme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';
import { ActionPressable, type ActionProps } from '../shared/ActionPressable';
import { ActionIconSlot, ActionLabel, type ActionIcon } from '../shared/ActionContent';
import { ToggleIconButton } from '../ToggleIconButton/ToggleIconButton';

type ToggleButtonType = 'default' | 'outline' | 'ghost';
export interface ToggleButtonProps extends Omit<ActionProps, 'loading'> {
  label: string;
  selected?: boolean;
  type?: ToggleButtonType;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  leadingIcon?: ActionIcon;
  trailingIcon?: ActionIcon;
  onSelectedChange?: (selected: boolean) => void;
}
export interface ToggleButtonGroupOption {
  key: string;
  label?: string;
  icon?: ActionIcon;
  selectedIcon?: ActionIcon;
  disabled?: boolean;
}
export interface ToggleButtonGroupProps {
  selectedIndex: number;
  onChange: (index: number) => void;
  options: ToggleButtonGroupOption[];
  type?: 'default' | 'iconOnly';
  state?: ActionProps['state'];
  buttonType?: Extract<ToggleButtonType, 'ghost' | 'outline'>;
  size?: 'small' | 'medium';
  disabled?: boolean;
  accessibilityLabel?: string;
}

export function ToggleButton({
  label,
  selected = false,
  type = 'ghost',
  size = 'medium',
  fullWidth,
  onPress,
  onSelectedChange,
  leadingIcon,
  trailingIcon,
  accessibilityLabel = label,
  ...props
}: ToggleButtonProps) {
  const theme = useTheme();
  const outline = !selected && type === 'outline';
  const color = theme.colors[selected ? 'text.inverse.primary' : 'text.brand'];
  const iconSize = Number(theme.tokens.sizing[size === 'small' ? 'sizing.icon.s' : 'sizing.icon.default']);
  return (
    <ActionPressable
      {...props}
      accessibilityLabel={accessibilityLabel}
      selected={selected}
      fullWidth={fullWidth}
      minHeight={Number(theme.tokens.sizing[`sizing.component.button.height.${size}`])}
      paddingHorizontal={theme.spacing['spacing.general.x3']}
      paddingVertical={theme.spacing['spacing.general.x1']}
      onPress={(event) => {
        onPress?.(event);
        onSelectedChange?.(!selected);
      }}
      appearance={({ pressed, hovered, disabled }) => {
        const active = pressed || hovered;
        return {
          backgroundColor: selected ? theme.colors['component.button.bg.primary'] : 'transparent',
          opacity: disabled ? INTERACTION.opacity['040'] : 1,
          borderWidth: outline ? theme.borderWidths[active ? 'border-width.emphasized' : 'border-width.standard'] : 0,
          borderColor: theme.colors[active ? 'border.brand' : 'border.secondary'],
          overlayColor: active
            ? theme.colors[
                selected
                  ? pressed
                    ? 'component.button.bg.pressed'
                    : 'component.button.bg.hover'
                  : outline
                    ? pressed
                      ? 'state-layer.press-brand'
                      : 'state-layer.hover-brand'
                    : pressed
                      ? 'state-layer.press'
                      : 'state-layer.hover'
              ]
            : undefined,
        };
      }}
    >
      {() => (
        <Box flexDirection="row" alignItems="center" justifyContent="center" maxWidth="100%">
          <ActionIconSlot icon={leadingIcon} size={iconSize} color={color} />
          <ActionLabel
            label={label}
            color={color}
            small={size === 'small'}
            padding={size === 'small' ? 'spacing.general.x1' : 'spacing.general.x2'}
          />
          <ActionIconSlot icon={trailingIcon} size={iconSize} color={color} />
        </Box>
      )}
    </ActionPressable>
  );
}

/** Figma's Segmented Control. Selection remains owned by the parent. */
export function ToggleButtonGroup({
  selectedIndex,
  onChange,
  options,
  type = 'default',
  state,
  buttonType = 'ghost',
  size = 'medium',
  disabled,
  accessibilityLabel,
}: ToggleButtonGroupProps) {
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      alignSelf="flex-start"
      maxWidth="100%"
      flexWrap="wrap"
      padding="spacing.general.x1"
      borderRadius="radius.full"
      backgroundColor="surface.primary-neutral"
      accessibilityRole="toolbar"
      accessibilityLabel={accessibilityLabel}
    >
      {options.map((option, index) => {
        const selected = index === selectedIndex;
        const onPress = () => {
          if (!selected) onChange(index);
        };
        return type === 'iconOnly' ? (
          <ToggleIconButton
            key={option.key}
            accessibilityLabel={option.label ?? option.key}
            selected={selected}
            type="ghost"
            size={size}
            state={state}
            disabled={disabled || option.disabled}
            icon={option.icon}
            selectedIcon={option.selectedIcon ?? option.icon}
            onPress={onPress}
          />
        ) : (
          <ToggleButton
            key={option.key}
            label={option.label ?? option.key}
            selected={selected}
            type={selected ? 'default' : buttonType}
            size={size}
            state={state}
            disabled={disabled || option.disabled}
            onPress={onPress}
          />
        );
      })}
    </Box>
  );
}
